import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getInstitution } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { useConsultation } from "../components/Consultation";
import PageHero from "../components/layout/PageHero";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import { Reveal } from "../components/motion/Reveal";

export default function InstituteDetail() {
  const { slug } = useParams();
  const openConsultation = useConsultation();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO(
    inst ? `${inst.name} — Suvera Academy` : "Institution — Suvera Academy",
    inst ? inst.description : "Institution profile."
  );

  useEffect(() => {
    setLoading(true);
    getInstitution(slug)
      .then(setInst)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen pt-32" data-testid="institute-loading">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
          <div className="h-[40vh] animate-pulse rounded-2xl bg-slate-200/70" />
        </div>
      </main>
    );
  }

  if (notFound || !inst) {
    return (
      <main className="hero-spotlight flex min-h-screen items-center" data-testid="institute-not-found">
        <div className="mx-auto max-w-xl px-6 text-center">
          <h1 className="font-display text-4xl font-semibold text-white">Institution Not Found</h1>
          <p className="mt-4 text-white/60">This institution profile does not exist. Browse the full directory instead.</p>
          <Link to="/institutes" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy" data-testid="institute-not-found-back">
            View All Institutions
          </Link>
        </div>
      </main>
    );
  }

  const initials = inst.name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  const location = inst.city && inst.city !== inst.country ? `${inst.city}, ${inst.country}` : inst.country;

  return (
    <main data-testid="institute-detail-page">
      <PageHero
        eyebrow={inst.type}
        title={inst.name}
        description={inst.description}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Institutes", to: "/institutes" }, { label: inst.name }]}
        testid="institute-hero"
      >
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white/80">
            <Icon name="MapPin" className="h-4 w-4" /> {location}
          </span>
          {inst.website && (
            <a
              href={inst.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-dot-amber hover:text-navy-dark"
              data-testid="institute-website-link"
            >
              Visit Official Website
              <Icon name="ArrowUpRight" className="h-4 w-4" />
            </a>
          )}
        </div>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="img-frame">
                <img src={inst.image} alt={`${inst.name} campus`} className="max-h-[60vh] w-full object-cover lg:max-h-[440px]" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-10 font-display text-2xl font-semibold text-navy-dark">About {inst.name}</h2>
              <p className="mt-4 leading-relaxed text-slate-600">{inst.description}</p>
              <p className="mt-4 leading-relaxed text-slate-600">
                For program availability, entry requirements, intake dates and tuition details for {inst.name}, speak with a Suvera Academy advisor.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7">
                <div className="flex items-center gap-4">
                  <span className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-100 bg-white">
                    <span className="flex h-full w-full items-center justify-center rounded-lg bg-navy font-display text-xl font-semibold text-white">{initials}</span>
                    {inst.logo && (
                      <img
                        src={inst.logo}
                        alt={`${inst.name} logo`}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full bg-white object-contain p-2"
                        onError={(e) => e.currentTarget.remove()}
                      />
                    )}
                  </span>
                  <div>
                    <h2 className="font-display text-xl font-semibold text-navy-dark">{inst.name}</h2>
                    <p className="text-sm text-slate-500">{inst.type} — {location}</p>
                  </div>
                </div>
                <h3 className="mt-7 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-slate-400">Popular Programs</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {inst.programs.map((p) => (
                    <span key={p} className="rounded-full border border-navy/15 bg-navy/5 px-3.5 py-1.5 text-sm font-medium text-navy">{p}</span>
                  ))}
                </div>
                <button
                  onClick={openConsultation}
                  className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light"
                  data-testid="institute-enquire-button"
                >
                  Enquire About {inst.name}
                  <Icon name="ArrowRight" className="h-4 w-4" />
                </button>
                {inst.website && (
                  <a
                    href={inst.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
                    data-testid="institute-visit-website-button"
                  >
                    Visit Website
                    <Icon name="ArrowUpRight" className="h-4 w-4" />
                  </a>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTASection />
    </main>
  );
}
